import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { Loader, NotificationType } from "@transfr-inc/dashboard-components";
import { PageLayout } from "@transfr-inc/dashboard-components/layouts";

import { WarningMessages } from "@transfr-inc/dashboard-components";
import {
  CreateLessonFailure,
  CreateLessonSuccess,
  CreateUnitFailure,
  CreateUnitSuccess,
  EditLessonFailure,
  EditLessonSuccess,
  EditUnitFailure,
  EditUnitSuccess,
} from "@components/utils/notification.constants";
import { SessionContext } from "@src/context";
import { services } from "@src/dependencies";
import { useNotification } from "@src/hooks";
import { useEditWarningModal } from "@transfr-inc/dashboard-components/hooks";
import { useApiRequest } from "@utils/http-client";

import {
  AddActivityModal,
  ContentType,
  CreateCourseStructureModal,
  EditUnitModal,
  DeletionModal,
  EditLessonModal,
  EditProgramStructureOrderModal,
  ProgramActivityLogModal,
  ProgramStructureSection,
  ResetCourseModal,
  EditLocation,
  Course,
} from "@transfr-inc/dashboard-components/curriculum";
import { getRoleFeatures } from "@constants/role";
import { EditProgramDetailsModal } from "./components/modals/edit-program-details.modal";
import { OrganizationsSection } from "./components/organizations.section";
import { ProgramDetailPageHeader } from "./components/program-detail.header";

import "./index.scss";

export const ProgramDetail = () => {
  const history = useHistory();
  const { programsService } = services;
  const { addNotification } = useNotification();
  const { currentUser, role } = useContext(SessionContext);
  const roleFeatures = getRoleFeatures(role);
  const { state } = useLocation();
  const { id } = useParams();
  const { breadcrumbLabel = "templates" } = state || {};
  const [loading, setLoading] = useState();
  const [programData, setProgramData] = useState(new Course());
  const [showCreateStructureModal, setShowCreateStructureModal] =
    useState(false);
  const [showEditStructureOrderModal, setShowEditStructureOrderModal] =
    useState(false);
  const [showActivityLogModal, setShowActivityLogModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);

  const [deleteState, setDeleteState] = useState({
    open: false,
    entityType: undefined,
    entityId: undefined,
    entityName: undefined,
  });

  const handleDelete = (entityType, entityId, entityName) => {
    if (programData?.organizations?.length > 0) {
      switch (entityType) {
        case "program": {
          openErrorDeleteModal();

          break;
        }
        case "unit": {
          openErrorDeleteUnitModal();

          break;
        }
        case "lesson": {
          openErrorDeleteLessonModal();

          break;
        }
        // No default
      }
    } else {
      setDeleteState({
        open: true,
        entityType,
        entityId,
        entityName,
      });
    }
  };

  const [errorDeleteModal, openErrorDeleteModal] = useEditWarningModal({
    title: WarningMessages.program.title,
    message: WarningMessages.program.delete,
    icon: ["fa-solid", "fa-clock-rotate-left"],
    iconColor: "danger-red",
  });

  const [errorDeleteUnitModal, openErrorDeleteUnitModal] = useEditWarningModal({
    title: WarningMessages.unit.title,
    message: WarningMessages.unit.delete,
    icon: ["fa-solid", "fa-clock-rotate-left"],
    iconColor: "danger-red",
  });

  const [errorDeleteLessonModal, openErrorDeleteLessonModal] =
    useEditWarningModal({
      title: WarningMessages.lesson.title,
      message: WarningMessages.lesson.delete,
      icon: ["fa-solid", "fa-clock-rotate-left"],
      iconColor: "danger-red",
    });

  const [courseMainStructure, setCourseMainStructure] = useState(); // Sets whether the course will have course units or course lessons;
  const [selectedStructureType, setSelectedStructureType] = useState(); // Sets the currently focused structure type;
  const [parentId, setParentId] = useState(); // Sets the parent id of the current structure type;

  const [editProgramDetailsModalOpen, setEditProgramDetailsModalOpen] =
    useState(false);
  const [showEditUnitModal, setShowEditUnitModal] = useState(false);
  const [selectedStructureData, setSelectedStructureData] = useState();
  const [newlyCreatedItemId, setNewlyCreatedItemId] = useState();
  const [showEditLessonModal, setShowEditLessonModal] = useState(false);
  const [showAddActivityModal, setShowAddActivityModal] = useState(false);
  const [addActivityLesson, setAddActivityLesson] = useState();
  const [addActivityContentType, setAddActivityContentType] = useState();

  const {
    loading: programMetadataLoading,
    response: programMetadata,
    sendRequest: getProgramMetadata,
  } = useApiRequest(() => programsService.getProgramMetadata(id), false);

  const {
    loading: programDataLoading,
    response: course,
    sendRequest: getCourse,
  } = useApiRequest(
    () =>
      programsService.getCourseDetails(id, { include_activity_metadata: true }),
    false,
  );

  const {
    loading: credentialsLoading,
    response: courseCredentials,
    sendRequest: getCourseCredentials,
  } = useApiRequest(() => programsService.getCourseCredentials(id), false);

  const {
    response: allCredentials,
    sendRequest: getAllCredentials,
    loading: allCredentialsLoading,
  } = useApiRequest(() => programsService.getAllCredentials(), false);

  const {
    response: allActivities,
    sendRequest: getAllActivities,
    loading: allActivitiesLoading,
  } = useApiRequest(() => programsService.getAllActivities(), false);

  const {
    response: allCourseActivities,
    sendRequest: getAllCourseActivities,
    loading: allCourseActivitiesLoading,
  } = useApiRequest(() => programsService.getAllCourseActivities(id), false);

  const {
    response: programActivityLogs,
    sendRequest: getProgramActivityLogs,
    loading: programActivityLogsLoading,
  } = useApiRequest(() => programsService.getProgramActivityLogs(id), false);

  useEffect(() => {
    if (course) {
      const hasLessons = course?.lessons && course.lessons.length > 0;
      const hasUnits = course?.units && course.units.length > 0;
      if (hasUnits) {
        setCourseMainStructure("units");
      } else if (hasLessons) {
        setCourseMainStructure("lessons");
      }
    }
  }, [course]);

  const onHandleDelete = async () => {
    const { entityType, entityId } = deleteState;
    if (!entityId || !entityType) return;

    setLoading(true);

    try {
      switch (entityType) {
        case "program": {
          await programsService.deleteCourseTemplate(entityId);
          history.push("/app/templates");
          break;
        }
        case "unit": {
          await programsService.deleteUnit(entityId, id);
          setProgramData((prevData) => ({
            ...prevData,
            units: prevData.units.filter((u) => u.id !== entityId),
          }));
          break;
        }
        case "lesson": {
          await programsService.deleteLesson(entityId, id);
          setProgramData((prevData) => {
            if (prevData.lessons?.some((l) => l.id === entityId)) {
              return {
                ...prevData,
                lessons: prevData.lessons.filter((l) => l.id !== entityId),
              };
            }
            return {
              ...prevData,
              units: prevData.units.map((unit) => ({
                ...unit,
                lessons: unit.lessons.filter((l) => l.id !== entityId),
              })),
            };
          });
          break;
        }
        default: {
          throw new Error("Unknown entity type");
        }
      }

      addNotification({
        type: NotificationType.success,
        message: `${entityType} deleted successfully`,
      });
    } catch (error) {
      console.error(`Error deleting ${entityType}`, error);
      addNotification({
        type: NotificationType.error,
        message: `Error deleting ${entityType}`,
      });
    } finally {
      setLoading(false);
      setDeleteState({
        open: false,
        entityType: undefined,
        entityId: undefined,
        entityName: undefined,
      });
    }
  };

  const handleResetCourse = async () => {
    setLoading(true);
    try {
      const updatedCourse = await programsService.sendProgramInfo({
        editedBy: currentUser.userId,
        editedFrom: EditLocation.EditProgramStructureOrderModal.id,
        course: { ...programData, units: [], lessons: [] },
      });
      setProgramData(new Course({ ...updatedCourse, id }));
      setCourseMainStructure();
      await getCourseCredentials();
      await getAllCourseActivities();
      addNotification({
        type: NotificationType.success,
        message: "Course reset",
      });
    } catch (error) {
      console.error("Error resetting course", error);
      addNotification({
        type: NotificationType.error,
        message: "Error resetting course",
      });
    } finally {
      setLoading(false);
      setShowResetModal(false);
    }
  };

  const { courseCredentialsList, mappedAllCredentials } = useMemo(() => {
    if (!courseCredentials || !allCredentials) {
      return { mappedAllCredentials: [], courseCredentialsList: [] };
    }

    const collectCredentials = () => {
      const ids = [];
      const add = (credentialId, parentId, credentialType) =>
        credentialId && ids.push({ credentialId, parentId, credentialType });

      add(
        courseCredentials.courseCredentialId,
        courseCredentials.courseId,
        courseCredentials.courseCredentialType,
      );

      for (const unit of courseCredentials.units || []) {
        add(unit.unitCredentialId, unit.unitId, unit.unitCredentialType);
        for (const lesson of unit.lessons || [])
          add(
            lesson.lessonCredentialId,
            lesson.lessonId,
            lesson.lessonCredentialType,
          );
      }

      for (const lesson of courseCredentials.lessons || [])
        add(
          lesson.lessonCredentialId,
          lesson.lessonId,
          lesson.lessonCredentialType,
        );

      return ids;
    };

    const ids = collectCredentials();

    for (const credential of allCredentials) {
      credential.disabled = ids.some((id) => id.credentialId === credential.id);
    }

    return {
      mappedAllCredentials: allCredentials,
      courseCredentialsList: ids,
    };
  }, [courseCredentials, allCredentials]);

  const getMappedActivitiesForLesson = useCallback(
    (lessonActivities = []) => {
      if (!allActivities) {
        return [];
      }

      const courseActivityIds = new Set(
        allCourseActivities?.map((a) => a.contentId) || [],
      );

      const lessonActivityIds = new Set(
        lessonActivities?.map((a) => a.id || a.data?.id) || [],
      );

      const allDisabledIds = new Set([
        ...courseActivityIds,
        ...lessonActivityIds,
      ]);

      return allActivities.map((activity) => ({
        ...activity,
        disabled: allDisabledIds.has(activity.id),
      }));
    },
    [allActivities, allCourseActivities],
  );

  useEffect(() => {
    if (id) {
      getCourse();
      getCourseCredentials();
      getProgramMetadata();
      getAllCredentials();
      getAllActivities();
      getAllCourseActivities();
    }
  }, [id]);

  useEffect(() => {
    if (course && programMetadata) {
      setProgramData(new Course({ ...course, ...programMetadata, id }));
    }
  }, [course, programMetadata, id]);

  useEffect(() => {
    if (
      programDataLoading ||
      credentialsLoading ||
      programMetadataLoading ||
      allCredentialsLoading ||
      allActivitiesLoading ||
      allCourseActivitiesLoading
    ) {
      setLoading(true);
    } else {
      setLoading();
    }
  }, [
    programDataLoading,
    credentialsLoading,
    programMetadataLoading,
    allCredentialsLoading,
    allActivitiesLoading,
    allCourseActivitiesLoading,
  ]);

  const onModifyCourseStructure = (
    structureType,
    modifyType,
    isTopLevel,
    structureData,
  ) => {
    if (modifyType === "add") {
      setSelectedStructureType(structureType);
      setShowCreateStructureModal(true);
    } else if (modifyType === "edit" && isTopLevel) {
      setShowEditStructureOrderModal(true);
      setSelectedStructureType(structureType);
    } else if (modifyType === "edit" && structureType === "unit") {
      setSelectedStructureType(structureType);
      setSelectedStructureData(structureData);
      setShowEditUnitModal(true);
    } else if (modifyType === "edit" && structureType === "lesson") {
      setSelectedStructureType(structureType);
      setSelectedStructureData(structureData);
      setShowEditLessonModal(true);
    }
  };

  const onApplyStructureOrderChanges = async (items) => {
    setLoading(true);
    const newStructure = {
      editedBy: currentUser.userId,
      editedFrom: EditLocation.EditProgramStructureOrderModal.id,
      course: {
        ...programData,
        [selectedStructureType]: items,
      },
    };

    try {
      const updatedCourse = await programsService.sendProgramInfo(newStructure);
      setProgramData(new Course({ ...updatedCourse, id }));
      await getCourseCredentials();
      await getAllCourseActivities();
      addNotification({
        type: NotificationType.success,
        message:
          selectedStructureType === "units"
            ? "Unit order updated."
            : "Lesson updated.",
      });
    } catch (error) {
      console.error(`Error updating ${selectedStructureType} order`, error);
      addNotification({
        type: NotificationType.error,
        message: `Error updating ${selectedStructureType} order`,
      });
    } finally {
      setLoading();
    }
    setShowEditStructureOrderModal(false);
  };

  const onCreateCourseStructure = async (formData) => {
    setLoading(true);
    const awardIds = formData.awardIds?.id
      ? [
          {
            id: formData.awardIds.id,
            type: formData.awardType,
          },
        ]
      : [];

    const activities = formData.activities
      ? formData.activities
          .flat()
          .map((activity) => {
            if (!activity?.data) return;
            if (!activity?.data?.contentId) {
              activity.data.contentId = activity.data?.id;
            }
            return activity?.data;
          })
          .filter(Boolean)
      : [];

    formData.activities = activities;

    const editLocation =
      selectedStructureType === "units"
        ? EditLocation.CreateUnitModal.id
        : EditLocation.CreateLessonModal.id;

    try {
      let newStructure;
      if (parentId) {
        // Find the parent unit and add the new structure to it
        const updatedUnits = programData.units.map((unit) => {
          if (unit.id === parentId) {
            const existingStructures = unit[selectedStructureType] || [];

            const formattedFormData = {
              ...formData,
              awardIds: awardIds,
            };

            return {
              ...unit,
              [selectedStructureType]: [
                ...existingStructures,
                formattedFormData,
              ],
            };
          }
          return unit;
        });

        newStructure = {
          editedBy: currentUser.userId,
          editedFrom: editLocation,
          course: {
            ...programData,
            units: updatedUnits,
          },
        };
      } else {
        // Add to the course level
        const existingStructures = programData[selectedStructureType] || [];

        const formattedFormData = {
          ...formData,
          awardIds: awardIds,
        };

        newStructure = {
          editedBy: currentUser.userId,
          editedFrom: editLocation,
          course: {
            ...programData,
            [selectedStructureType]: [...existingStructures, formattedFormData],
          },
        };
      }

      const newCourseData = await programsService.sendProgramInfo(newStructure);
      setProgramData(new Course({ ...newCourseData, id }));
      await getCourseCredentials();
      await getAllCourseActivities();

      const newItems = newCourseData[selectedStructureType] || [];
      const lastCreatedItem = newItems.at(-1);

      if (lastCreatedItem?.id) {
        setNewlyCreatedItemId(lastCreatedItem.id);
      }

      addNotification({
        type: NotificationType.success,
        message:
          selectedStructureType === "units"
            ? CreateUnitSuccess(lastCreatedItem)
            : CreateLessonSuccess(lastCreatedItem),
      });
    } catch {
      addNotification({
        type: NotificationType.error,
        message:
          selectedStructureType === "units"
            ? CreateUnitFailure()
            : CreateLessonFailure(),
      });
    } finally {
      setLoading(false);
      setParentId();
      setShowCreateStructureModal(false);
    }
  };

  const handleCreateUnitModalClose = () => {
    setParentId();
    setShowCreateStructureModal(false);
  };

  const handleEditStructureOrderModalClose = () => {
    setShowEditStructureOrderModal(false);
  };

  const handleEditUnitModalClose = () => {
    setShowEditUnitModal(false);
    setSelectedStructureData();
  };

  const onApplyUnitChanges = async (formData) => {
    setLoading(true);
    const awardIds = formData.awardIds?.id
      ? [
          {
            id: formData.awardIds.id,
            type: formData.awardType,
          },
        ]
      : [];
    const editLocation = EditLocation.EditUnitModal.id;

    try {
      const updatedUnits = programData.units.map((unit) => {
        if (unit.id === selectedStructureData.id) {
          return {
            ...unit,
            title: formData.title,
            awardIds: awardIds,
            lessons: formData.lessons || [],
          };
        }
        return unit;
      });

      const newStructure = {
        editedBy: currentUser.userId,
        editedFrom: editLocation,
        course: {
          ...programData,
          units: updatedUnits,
        },
      };

      const updatedCourse = await programsService.sendProgramInfo(newStructure);
      setProgramData(new Course({ ...updatedCourse, id }));
      await getCourseCredentials();
      await getAllCourseActivities();

      addNotification({
        type: NotificationType.success,
        message: EditUnitSuccess(formData),
      });
    } catch {
      addNotification({
        type: NotificationType.error,
        message: EditUnitFailure(formData),
      });
    } finally {
      setLoading();
      setShowEditUnitModal(false);
      setSelectedStructureData();
    }
  };

  const handleEditLessonModalClose = () => {
    setShowEditLessonModal(false);
    setSelectedStructureData();
  };

  const onApplyLessonChanges = async (formData) => {
    setLoading(true);

    const editLocation = EditLocation.EditLessonModal.id;
    const awardIds = formData.awardIds?.id
      ? [
          {
            id: formData.awardIds.id,
            type: formData.awardType,
          },
        ]
      : [];
    const activities = formData.activities
      ? formData.activities
          .flat()
          .map((activity) => {
            const activityData = activity?.data || activity;
            if (!activityData) return;
            if (!activityData?.contentId) {
              activityData.contentId = activityData?.id;
            }
            return activityData;
          })
          .filter(Boolean)
      : [];

    formData.activities = activities;

    try {
      let newStructure;

      if (courseMainStructure === "lessons") {
        const updatedLessons = programData.lessons.map((lesson) => {
          if (lesson.id === selectedStructureData.id) {
            return {
              ...lesson,
              title: formData.title,
              awardIds: awardIds,
              activities: activities || [],
            };
          }
          return lesson;
        });

        newStructure = {
          editedBy: currentUser.userId,
          editedFrom: editLocation,
          course: {
            ...programData,
            lessons: updatedLessons,
          },
        };
      } else if (courseMainStructure === "units") {
        const updatedUnits = programData.units.map((unit) => {
          const updatedLessons = unit.lessons.map((lesson) => {
            if (lesson.id === selectedStructureData.id) {
              return {
                ...lesson,
                title: formData.title,
                awardIds: awardIds,
                activities: activities || [],
              };
            }
            return lesson;
          });

          return {
            ...unit,
            lessons: updatedLessons,
          };
        });

        newStructure = {
          editedBy: currentUser.userId,
          editedFrom: editLocation,
          course: {
            ...programData,
            units: updatedUnits,
          },
        };
      }

      const updatedCourse = await programsService.sendProgramInfo(newStructure);
      setProgramData(new Course({ ...updatedCourse, id }));
      await getCourseCredentials();
      await getAllCourseActivities();

      addNotification({
        type: NotificationType.success,
        message: EditLessonSuccess(formData),
      });
    } catch {
      addNotification({
        type: NotificationType.error,
        message: EditLessonFailure(formData),
      });
    } finally {
      setLoading();
      setShowEditLessonModal(false);
      setSelectedStructureData();
    }
  };

  const onEditProgramDetails = async () => {
    await Promise.all([getProgramMetadata(), getCourseCredentials()]);

    setProgramData(new Course({ ...programData, ...programMetadata, id }));
  };

  const onAddActivity = (lesson, contentType) => {
    setAddActivityLesson(lesson);
    setAddActivityContentType(contentType);
    setShowAddActivityModal(true);
  };

  const onAddActivities = async (selectedActivities) => {
    setLoading(true);
    const lessonId = addActivityLesson?.id;
    const mergeIntoLesson = (lesson) => ({
      ...lesson,
      activities: [...(lesson.activities || []), ...selectedActivities].map(
        (activity) => {
          const activityData = activity?.data || activity;
          if (!activityData.contentId) {
            activityData.contentId = activityData.id;
          }
          return activityData;
        },
      ),
    });

    try {
      const course =
        courseMainStructure === "lessons"
          ? {
              ...programData,
              lessons: programData.lessons.map((lesson) =>
                lesson.id === lessonId ? mergeIntoLesson(lesson) : lesson,
              ),
            }
          : {
              ...programData,
              units: programData.units.map((unit) => ({
                ...unit,
                lessons: unit.lessons.map((lesson) =>
                  lesson.id === lessonId ? mergeIntoLesson(lesson) : lesson,
                ),
              })),
            };

      const updatedCourse = await programsService.sendProgramInfo({
        editedBy: currentUser.userId,
        editedFrom: EditLocation.EditLessonModal.id,
        course,
      });
      setProgramData(new Course({ ...updatedCourse, id }));
      await getAllCourseActivities();

      addNotification({
        type: NotificationType.success,
        message:
          selectedActivities.length > 1
            ? "Activities added."
            : "Activity added.",
      });
    } catch {
      addNotification({
        type: NotificationType.error,
        message: "Error adding activities.",
      });
    } finally {
      setLoading();
      setShowAddActivityModal(false);
      setAddActivityLesson();
    }
  };

  return (
    <PageLayout
      className="program-detail-page"
      header={
        <ProgramDetailPageHeader
          data={programData}
          breadcrumb={breadcrumbLabel}
          credentials={courseCredentials}
          onEditProgramDetails={() => setEditProgramDetailsModalOpen(true)}
          handleDelete={handleDelete}
        />
      }
    >
      {loading && <Loader overlay></Loader>}
      <div className="program-detail-body">
        <div className="text-with-info">
          <label>
            Changes made will be automatically reflected on customer menus
            <sup>*</sup>
          </label>
        </div>
        <OrganizationsSection
          data={programData}
          loading={loading}
        ></OrganizationsSection>
        <ProgramStructureSection
          data={programData}
          onModifyCourseStructure={onModifyCourseStructure}
          selectedStructureType={selectedStructureType}
          setParentId={setParentId}
          courseMainStructure={courseMainStructure}
          setCourseMainStructure={setCourseMainStructure}
          newlyCreatedItemId={newlyCreatedItemId}
          setNewlyCreatedItemId={setNewlyCreatedItemId}
          handleDelete={handleDelete}
          mappedAllCredentials={mappedAllCredentials}
          setShowActivityLogModal={setShowActivityLogModal}
          onAddActivity={onAddActivity}
          activityContentTypes={[
            ContentType.InHeadset.id,
            ContentType.OutOfHeadset.id,
            ContentType.CurriculumResources.id,
          ]}
          canEdit={roleFeatures?.canEditProgram}
          onResetCourse={() => setShowResetModal(true)}
        ></ProgramStructureSection>
      </div>
      {editProgramDetailsModalOpen && (
        <EditProgramDetailsModal
          program={programData}
          open={editProgramDetailsModalOpen}
          onEditProgramDetailsHandler={onEditProgramDetails}
          onCancel={() => setEditProgramDetailsModalOpen()}
          showNotification={addNotification}
          preventClose={true}
          mappedAllCredentials={mappedAllCredentials}
          courseCredentialsList={courseCredentialsList}
        />
      )}
      <CreateCourseStructureModal
        open={showCreateStructureModal}
        onClose={handleCreateUnitModalClose}
        courseCredentialsList={courseCredentialsList}
        onCreateCourseStructure={onCreateCourseStructure}
        selectedStructureType={selectedStructureType}
        loading={loading}
        mappedAllCredentials={mappedAllCredentials}
        preventClose={true}
      />
      <EditProgramStructureOrderModal
        loading={loading}
        items={
          courseMainStructure === "units"
            ? programData.units
            : programData.lessons
        }
        open={showEditStructureOrderModal}
        courseCredentialsList={courseCredentialsList}
        onClose={handleEditStructureOrderModalClose}
        onApplyChanges={onApplyStructureOrderChanges}
        confirmRequired={!!programData?.organizations?.length}
        program={programData}
        structureType={selectedStructureType}
        preventClose={true}
        async
      />
      {showEditUnitModal && (
        <EditUnitModal
          open={showEditUnitModal}
          onClose={handleEditUnitModalClose}
          onApplyChanges={onApplyUnitChanges}
          async
          unit={selectedStructureData}
          lessons={selectedStructureData?.lessons}
          confirmRequired={!!programData?.organizations?.length}
          mappedAllCredentials={mappedAllCredentials}
          preventClose={true}
          courseCredentialsList={courseCredentialsList}
        />
      )}
      {showEditLessonModal && (
        <EditLessonModal
          open={showEditLessonModal}
          onClose={handleEditLessonModalClose}
          onApplyChanges={onApplyLessonChanges}
          async
          lesson={selectedStructureData}
          confirmRequired={!!programData?.organizations?.length}
          mappedAllCredentials={mappedAllCredentials}
          selectedActivities={selectedStructureData?.activities}
          preventClose={true}
          courseCredentialsList={courseCredentialsList}
        />
      )}
      {showAddActivityModal && (
        <AddActivityModal
          open={showAddActivityModal}
          onClose={() => {
            setShowAddActivityModal(false);
            setAddActivityLesson();
            setAddActivityContentType();
          }}
          onApplyChanges={onAddActivities}
          async
          contentType={addActivityContentType}
          allActivities={getMappedActivitiesForLesson(
            addActivityLesson?.activities,
          )}
          preventClose={true}
        />
      )}
      {errorDeleteModal}
      {errorDeleteUnitModal}
      {errorDeleteLessonModal}
      {showActivityLogModal && (
        <ProgramActivityLogModal
          open={showActivityLogModal}
          onClose={() => setShowActivityLogModal(false)}
          programId={id}
          logs={programActivityLogs}
          loading={programActivityLogsLoading}
          getActivityLogs={getProgramActivityLogs}
        />
      )}
      <DeletionModal
        open={deleteState.open}
        onCancel={() =>
          setDeleteState({
            open: false,
            entityType: undefined,
            entityId: undefined,
            entityName: undefined,
          })
        }
        onDeleteClick={onHandleDelete}
        entityType={deleteState.entityType}
        name={deleteState.entityName}
        preventClose={true}
      />
      <ResetCourseModal
        open={showResetModal}
        onCancel={() => setShowResetModal(false)}
        onResetClick={handleResetCourse}
        preventClose={true}
      />
    </PageLayout>
  );
};
